import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';

import { MAIN_DOMAIN } from '@app/app.constants';
import { Currency, Lesson, Question, StudentExExerciseStat } from '@df/interfaces';
import { interval } from 'rxjs/internal/observable/interval';
import { switchMap } from 'rxjs/internal/operators/switchMap';

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private currentCurrency: Currency;

    constructor(private http: HttpClient) {}

    pollingPupilGet(lesson_id: number, intTime: number): Observable<any> {
        return interval(intTime).pipe(
            switchMap(() => this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`))
        );
    }

    getLesson(lesson_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`);
    }

    getCurrentPupil(studentId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/current/${studentId}/`);
    }

    getTaskFiles(): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/file/`);
    }

    getSubjectFiles(subjectTitle: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/files/${subjectTitle}/`);
    }

    getGreetings(state: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/main/greeting/random/?is_success=${state}`);
    }

    getCard(cardId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/card/${cardId}/`);
    }

    getCardToNumber(cardNumber: number, lessonId: number, studentId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/card/number/${cardNumber}/${lessonId}/${studentId}/`);
    }

    checkCard(cardId: number, studentId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/card/check_status/${cardId}/${studentId}/`);
    }

    getTaskResultCount(lesson_id: number, pupil_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/results/count/${lesson_id}/${pupil_id}/`);
    }

    getPrintTask(targetId: number, isLesson: boolean) {
        if (isLesson) {
            return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/box/print/?lesson=${targetId}`);
        } else {
            return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/box/print/?id=${targetId}`);
        }
    }

    getTaskBoxById(box_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/box/${box_id}/`);
    }

    checkCorrectAnswer(answer: string, exercise_or_question: any): boolean {
        answer = this.trimAnswer(answer);
        for (const answ of exercise_or_question.answers) {
            const short = this.trimAnswer(answ.shortAnswer);
            const long = this.trimAnswer(answ.longAnswer);
            let isCorrect = answer === long || answer === short;
            if (answer && parseFloat(short) && short.match(/\d+/g).length === 1) {
                isCorrect = parseFloat(short) === parseFloat(answer);
            }
            return isCorrect;
        }
        return false;
    }

    trimAnswer(str: string): string {
        if (!str) {
            return '';
        }
        return str.replace(/\s/g, '');
    }

    checkCorrectTaskQuestion(answer: string, question: Question): boolean {
        for (const answ of question.answers) {
            const shortAnswerStr = answ.shortAnswer.split(' ')[0];
            return answer === shortAnswerStr || answer === answ.shortAnswer || answer === answ.fullAnswer;
        }
        return false;
    }

    setCardResults(data: any): Observable<any> {
        return this.http.post<any>(`${MAIN_DOMAIN}/api/tasks/results/card/`, data);
    }

    getStudentExercisesAnalytics(lessonID: number, studentID: number): Observable<any> {
        return this.http.get<any>(
            `${MAIN_DOMAIN}/api/main_analytics/student-exercises/analytics/${lessonID}/${studentID}/`
        );
    }

    getCurrencyForLesson(lesson: Lesson): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/accounts/currency/?epoch=${lesson.epoch}`);
    }

    getEpochCurrency(epochId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/accounts/currency/?epoch=${epochId}`);
    }

    moneyOperation(
        studentId: number,
        amount: number,
        operation: string,
        comment: string = '',
        card_id = null
    ): Observable<any> {
        return this.http.post<any>(`${MAIN_DOMAIN}/api/accounts/account/student/${studentId}/`, {
            amount: amount,
            operation: operation,
            comment: comment,
            card_id: card_id
        });
    }

    rollBackTransactions(card_id: number, currentUser = '', del: boolean): Observable<any> {
        return this.http.post<any>(`${MAIN_DOMAIN}/api/accounts/account/rollback/`, {
            card_id: card_id,
            current_user: currentUser,
            delete_card: del
        });
    }

    getCurrency(): Currency | null {
        return this.currentCurrency;
    }

    setCurrency(currency: Currency): void {
        this.currentCurrency = currency;
    }

    removeCurrency(): void {
        sessionStorage.setItem('currentCurrency', '');
    }

    deleteCard(cardId: number, prevResultId): Observable<boolean> {
        const data = {
            card_id: cardId,
            prev_result_id: prevResultId
        };
        return this.http.post<any>(`${MAIN_DOMAIN}/api/tasks/card/delete/`, data);
    }

    converSecondsToMinuteString(sec: number): string {
        if (sec === 0) {
            return '00:00';
        }
        const min = Math.floor(sec / 60);
        const remainder_seconds = sec - min * 60;
        let seconds_str: string = null;
        if (remainder_seconds <= 9) {
            seconds_str = `0${remainder_seconds}`;
        } else {
            seconds_str = `${remainder_seconds}`;
        }
        return `0${min}:${seconds_str}`;
    }

    getAvailableCardIdForExExercise(lessonId, pupilId): Observable<number> {
        if (pupilId) {
            return this.http.get<number>(`${MAIN_DOMAIN}/api/tasks/available_card/${lessonId}/${pupilId}/`);
        }
        return this.http.get<number>(`${MAIN_DOMAIN}/api/tasks/available_card/${lessonId}/1/`);
    }

    updateCardResult(cardId: number, isExtraExercise: string): Observable<boolean> {
        return this.http.post<boolean>(`${MAIN_DOMAIN}/api/tasks/update_card_result/`, {
            card_id: cardId,
            is_extra_exercise: isExtraExercise
        });
    }

    getExExerciseStatForStudent(lessonId: number, studentId: number): Observable<StudentExExerciseStat> {
        return this.http.get<StudentExExerciseStat>(
            `${MAIN_DOMAIN}/api/tasks/ex_exercise/stat/${lessonId}/${studentId}/`
        );
    }

    getRandomBoxCard(boxId: number, studentId: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/tasks/random_box_card/${boxId}/${studentId}/`);
    }
}
